import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaChartBar,
  FaChartLine,
  FaClock,
  FaEdit,
  FaEuroSign,
  FaPlus,
  FaTint,
  FaTrashAlt,
} from "react-icons/fa";
import axios from "../axiosConfig";
import "../styles/TruckFuelTab.css";
import { useTranslation } from "react-i18next";

const FUEL_DETAILS_CACHE_PREFIX =
  "truck:fuel:details:v1:";

const FUEL_DETAILS_CACHE_TS_PREFIX =
  "truck:fuel:details:ts:v1:";

const FUEL_DETAILS_CACHE_TTL_MS =
  24 *
  60 *
  60 *
  1000;

function getFuelDetailsCacheKey(
  truckId
) {
  return `${FUEL_DETAILS_CACHE_PREFIX}${truckId}`;
}

function getFuelDetailsCacheTimestampKey(
  truckId
) {
  return `${FUEL_DETAILS_CACHE_TS_PREFIX}${truckId}`;
}

function readFuelDetailsCache(
  truckId
) {
  if (
    !truckId ||
    typeof window ===
      "undefined"
  ) {
    return null;
  }

  try {
    const raw =
      window.localStorage.getItem(
        getFuelDetailsCacheKey(
          truckId
        )
      );

    if (
      !raw
    ) {
      return null;
    }

    const parsed =
      JSON.parse(
        raw
      );

    if (
      !parsed ||
      typeof parsed !==
        "object"
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeFuelDetailsCache(
  truckId,
  data
) {
  if (
    !truckId ||
    !data ||
    typeof window ===
      "undefined"
  ) {
    return;
  }

  try {
    window.localStorage.setItem(
      getFuelDetailsCacheKey(
        truckId
      ),
      JSON.stringify({
        logs:
          Array.isArray(
            data.logs
          )
            ? data.logs
            : [],

        summary:
          data.summary ||
          null,
      })
    );

    window.localStorage.setItem(
      getFuelDetailsCacheTimestampKey(
        truckId
      ),
      String(
        Date.now()
      )
    );
  } catch {
    // Cache indisponível.
  }
}

function isFuelDetailsCacheFresh(
  truckId
) {
  if (
    !truckId ||
    typeof window ===
      "undefined"
  ) {
    return false;
  }

  try {
    const savedAt =
      Number(
        window.localStorage.getItem(
          getFuelDetailsCacheTimestampKey(
            truckId
          )
        ) ||
          0
      );

    return (
      savedAt >
        0 &&
      Date.now() -
        savedAt <
        FUEL_DETAILS_CACHE_TTL_MS
    );
  } catch {
    return false;
  }
}

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  odometerKm: "",
  litresAdded: "",
  pricePerLitre: "",
  totalCost: "",
  isFullTank: false,
  stationName: "",
  notes: "",
});

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function fmtNumber(value, decimals = 2) {
  const n = toNumber(value);
  if (n === null) return "—";
  return n.toFixed(decimals);
}

function fmtMoney(value) {
  const n = toNumber(value);
  if (n === null) return "—";
  return `${n.toFixed(2)} €`;
}

function fmtDate(date) {
  if (!date) return "—";
  return String(date).split("T")[0];
}

function TruckFuelTab({ truckId, truckLabel }) {
  const { t } = useTranslation("trucks");

  /*
   * Lemos o combustível sincronamente.
   *
   * Assim o primeiro render já nasce com os
   * abastecimentos e resumo que tínhamos guardados.
   */
  const initialFuelData =
    useMemo(
      () =>
        readFuelDetailsCache(
          truckId
        ),
      [
        truckId,
      ]
    );

  const [
    logs,
    setLogs,
  ] =
    useState(
      () =>
        Array.isArray(
          initialFuelData?.logs
        )
          ? initialFuelData.logs
          : []
    );

  const [
    summary,
    setSummary,
  ] =
    useState(
      () =>
        initialFuelData?.summary ||
        null
    );

  const [
    loading,
    setLoading,
  ] =
    useState(
      false
    );

  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchFuelLogs =
    useCallback(
      async ({
        showLoading =
          false,
      } = {}) => {
        if (
          !truckId
        ) {
          return;
        }

        if (
          showLoading
        ) {
          setLoading(
            true
          );
        }

        setErrorMessage(
          ""
        );

        try {
          const {
            data,
          } =
            await axios.get(
              `/truck-fuel-logs/truck/${truckId}`
            );

          const nextLogs =
            Array.isArray(
              data?.logs
            )
              ? data.logs
              : [];

          const nextSummary =
            data?.summary ||
            null;

          /*
           * Primeiro atualizamos a cache.
           */
          writeFuelDetailsCache(
            truckId,
            {
              logs:
                nextLogs,

              summary:
                nextSummary,
            }
          );

          /*
           * Depois atualizamos React.
           *
           * Como os dados antigos continuam visíveis
           * durante o GET, não existe flash.
           */
          setLogs(
            nextLogs
          );

          setSummary(
            nextSummary
          );
        } catch (
          error
        ) {
          console.error(
            "[TruckFuelTab] fetch error:",
            error
          );

          /*
           * Se já temos cache visível,
           * não substituímos os dados por um erro.
           */
          const cached =
            readFuelDetailsCache(
              truckId
            );

          if (
            !cached
          ) {
            setErrorMessage(
              t(
                "fuel.errors.loadFailed",
                "Não foi possível carregar os abastecimentos."
              )
            );
          }
        } finally {
          if (
            showLoading
          ) {
            setLoading(
              false
            );
          }
        }
      },
      [
        truckId,
        t,
      ]
    );

  useEffect(() => {
    /*
     * Se já temos cache, deixamo-la visível
     * e apenas sincronizamos silenciosamente.
     *
     * Não colocamos loading=true.
     */
    fetchFuelLogs({
      showLoading:
        false,
    });
  }, [
    fetchFuelLogs,
  ]);

  const openCreateModal = () => {
    setEditingLog(null);
    setForm(emptyForm());
    setFieldErrors({});
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const openEditModal = (log) => {
    setEditingLog(log);
    setForm({
      date: fmtDate(log.date),
      odometerKm: log.odometerKm ?? "",
      litresAdded: log.litresAdded ?? "",
      pricePerLitre: log.pricePerLitre ?? "",
      totalCost: log.totalCost ?? "",
      isFullTank: Boolean(log.isFullTank),
      stationName: log.stationName ?? "",
      notes: log.notes ?? "",
    });
    setFieldErrors({});
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditingLog(null);
    setFieldErrors({});
    setForm(emptyForm());
  };

  const validate = () => {
    const errors = {};

    if (!form.date) errors.date = t("fuel.errors.dateRequired", "A data é obrigatória.");

    const odometer = toNumber(form.odometerKm);
    if (odometer === null || odometer < 0) {
      errors.odometerKm = t("fuel.errors.odometerRequired", "O odómetro é obrigatório.");
    }

    const litres = toNumber(form.litresAdded);
    if (litres === null || litres <= 0) {
      errors.litresAdded = t("fuel.errors.litresRequired", "Os litros são obrigatórios.");
    }

    const price = toNumber(form.pricePerLitre);
    if (form.pricePerLitre !== "" && (price === null || price < 0)) {
      errors.pricePerLitre = t("fuel.errors.invalidPrice", "Preço inválido.");
    }

    const total = toNumber(form.totalCost);
    if (form.totalCost !== "" && (total === null || total < 0)) {
      errors.totalCost = t("fuel.errors.invalidTotal", "Total inválido.");
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "litresAdded" || name === "pricePerLitre") {
        const litres = toNumber(name === "litresAdded" ? value : next.litresAdded);
        const price = toNumber(name === "pricePerLitre" ? value : next.pricePerLitre);

        if (litres !== null && price !== null && litres > 0 && price >= 0) {
          next.totalCost = (litres * price).toFixed(2);
        }
      }

      return next;
    });

    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      setErrorMessage(t("fuel.errors.fillRequired", "Preenche os campos obrigatórios."));
      return;
    }

    setSaving(true);
    setErrorMessage("");

    const payload = {
      truckId: Number(truckId),
      date: form.date,
      odometerKm: form.odometerKm,
      litresAdded: form.litresAdded,
      pricePerLitre: form.pricePerLitre || null,
      totalCost: form.totalCost || null,
      isFullTank: Boolean(form.isFullTank),
      stationName: form.stationName || null,
      notes: form.notes || null,
    };

    try {
      if (editingLog?.id) {
        await axios.put(`/truck-fuel-logs/${editingLog.id}`, payload);
      } else {
        await axios.post("/truck-fuel-logs", payload);
      }

      closeModal();
      await fetchFuelLogs();
    } catch (error) {
      console.error("[TruckFuelTab] save error:", error);

      const backendErrors = error.response?.data?.errors;
      if (Array.isArray(backendErrors)) {
        const mapped = {};
        backendErrors.forEach((err) => {
          if (err.field) mapped[err.field] = err.message;
        });
        setFieldErrors(mapped);
      }

      setErrorMessage(
        error.response?.data?.message ||
          t("fuel.errors.saveFailed", "Não foi possível guardar o abastecimento.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (log) => {
    const ok = window.confirm(
      t("fuel.confirmDelete", "Tens a certeza de que queres apagar este abastecimento?")
    );
    if (!ok) return;

    try {
      await axios.delete(`/truck-fuel-logs/${log.id}`);
      await fetchFuelLogs();
    } catch (error) {
      console.error("[TruckFuelTab] delete error:", error);
      setErrorMessage(t("fuel.errors.deleteFailed", "Não foi possível apagar o abastecimento."));
    }
  };

  const latestStatus = useMemo(() => {
    if (!summary) return "—";

    if (summary.pendingSinceLastFullTank) {
      return t("fuel.pending", "Pendente até ao próximo depósito cheio");
    }

    if (summary.lastConsumptionL100Km !== null && summary.lastConsumptionL100Km !== undefined) {
      return `${fmtNumber(summary.lastConsumptionL100Km, 2)} L/100km`;
    }

    return t("fuel.noCalculatedCycle", "Ainda sem ciclo completo");
  }, [summary, t]);

  return (
    <section
      className="truck-fuel-section"
      aria-label={
        truckLabel
          ? `${t("fuel.title", "Combustível")} — ${truckLabel}`
          : t("fuel.title", "Combustível")
      }
    >
      <div className="truck-fuel-header">
        <h2 className="truck-fuel-title">
          3. {t("fuel.title", "Combustível")}
        </h2>

        <button
          type="button"
          className="truck-fuel-add-btn"
          onClick={openCreateModal}
        >
          <FaPlus />
          {t("fuel.add", "Adicionar abastecimento")}
        </button>
      </div>

      {errorMessage && <div className="truck-fuel-error">{errorMessage}</div>}

      <div className="truck-fuel-cards">
        <div className="truck-fuel-card">
          <span className="truck-fuel-card-icon">
            <FaChartLine />
          </span>

          <div className="truck-fuel-card-copy">
            <span>{t("fuel.cards.lastConsumption", "Último consumo")}</span>
            <strong>{latestStatus}</strong>
          </div>
        </div>

        <div className="truck-fuel-card">
          <span className="truck-fuel-card-icon">
            <FaChartBar />
          </span>

          <div className="truck-fuel-card-copy">
            <span>{t("fuel.cards.average", "Média calculada")}</span>
            <strong>
              {summary?.averageConsumptionL100Km
                ? `${fmtNumber(summary.averageConsumptionL100Km, 2)} L/100km`
                : "—"}
            </strong>
          </div>
        </div>

        <div className="truck-fuel-card">
          <span className="truck-fuel-card-icon">
            <FaTint />
          </span>

          <div className="truck-fuel-card-copy">
            <span>{t("fuel.cards.totalLitres", "Total litros")}</span>
            <strong>
              {summary?.totalLitres
                ? `${fmtNumber(summary.totalLitres, 2)} L`
                : "—"}
            </strong>
          </div>
        </div>

        <div className="truck-fuel-card">
          <span className="truck-fuel-card-icon">
            <FaEuroSign />
          </span>

          <div className="truck-fuel-card-copy">
            <span>{t("fuel.cards.totalCost", "Total custo")}</span>
            <strong>
              {summary?.totalCost
                ? fmtMoney(summary.totalCost)
                : "—"}
            </strong>
          </div>
        </div>

        <div className="truck-fuel-card">
          <span className="truck-fuel-card-icon">
            <FaClock />
          </span>

          <div className="truck-fuel-card-copy">
            <span>{t("fuel.cards.costPerKm", "Último custo/km")}</span>
            <strong>
              {summary?.lastCostPerKm
                ? `${fmtNumber(summary.lastCostPerKm, 4)} €/km`
                : "—"}
            </strong>
          </div>
        </div>
      </div>

      <div className="truck-fuel-table-wrap">
        {logs.length === 0 ? (
          <div className="truck-fuel-empty">
            {t(
              "fuel.empty",
              "Ainda não existem abastecimentos registados para este camião."
            )}
          </div>
        ) : (
          <table className="truck-fuel-table">
            <thead>
              <tr>
                <th>{t("fuel.table.date", "Data")}</th>
                <th>{t("fuel.table.odometer", "Km")}</th>
                <th>{t("fuel.table.litres", "Litros")}</th>
                <th>{t("fuel.table.fullTank", "Cheio")}</th>
                <th>{t("fuel.table.price", "€/L")}</th>
                <th>{t("fuel.table.total", "Total")}</th>
                <th>{t("fuel.table.consumption", "Consumo")}</th>
                <th>{t("fuel.table.status", "Estado")}</th>
                <th>{t("fuel.table.actions", "Ações")}</th>
              </tr>
            </thead>

            <tbody>
              {logs.map((log) => {
                const hasConsumption =
                  log.consumptionL100Km !== null && log.consumptionL100Km !== undefined;

                return (
                  <tr key={log.id}>
                    <td>{fmtDate(log.date)}</td>
                    <td>{fmtNumber(log.odometerKm, 0)}</td>
                    <td>{fmtNumber(log.litresAdded, 2)} L</td>
                    <td>
                      <span className={log.isFullTank ? "truck-fuel-badge full" : "truck-fuel-badge partial"}>
                        {log.isFullTank
                          ? t("fuel.yes", "Sim")
                          : t("fuel.no", "Não")}
                      </span>
                    </td>
                    <td>{log.pricePerLitre ? fmtNumber(log.pricePerLitre, 4) : "—"}</td>
                    <td>{log.totalCost ? fmtMoney(log.totalCost) : "—"}</td>
                    <td>{hasConsumption ? `${fmtNumber(log.consumptionL100Km, 2)} L/100km` : "—"}</td>
                    <td>
                      <span
                        className={`truck-fuel-status-badge ${
                          hasConsumption
                            ? "calculated"
                            : log.isFullTank
                            ? "reference"
                            : "pending"
                        }`}
                      >
                        {hasConsumption
                          ? t("fuel.calculated", "Calculado")
                          : log.isFullTank
                          ? t("fuel.startPoint", "Ponto de referência")
                          : t("fuel.pendingShort", "Pendente")}
                      </span>
                    </td>
                    <td className="truck-fuel-actions">
                      <button type="button" onClick={() => openEditModal(log)} title={t("fuel.edit", "Editar")}>
                        <FaEdit />
                      </button>
                      <button type="button" onClick={() => handleDelete(log)} title={t("fuel.delete", "Apagar")}>
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen && (
        <div className="truck-fuel-modal-overlay" onMouseDown={closeModal}>
          <div className="truck-fuel-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="truck-fuel-modal-header">
              <h3>
                {editingLog
                  ? t("fuel.modal.editTitle", "Editar abastecimento")
                  : t("fuel.modal.createTitle", "Adicionar abastecimento")}
              </h3>
              <button type="button" className="truck-fuel-modal-close" onClick={closeModal}>
                ×
              </button>
            </div>

            <form className="truck-fuel-form" onSubmit={handleSubmit}>
              <div className="truck-fuel-form-grid">
                <div className="truck-fuel-field">
                  <label>{t("fuel.fields.date", "Data")}*</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => handleFormChange("date", e.target.value)}
                    className={fieldErrors.date ? "input-error" : ""}
                  />
                  {fieldErrors.date && <p>{fieldErrors.date}</p>}
                </div>

                <div className="truck-fuel-field">
                  <label>{t("fuel.fields.odometer", "Odómetro")}*</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.odometerKm}
                    onChange={(e) => handleFormChange("odometerKm", e.target.value)}
                    className={fieldErrors.odometerKm ? "input-error" : ""}
                    placeholder="100000"
                  />
                  {fieldErrors.odometerKm && <p>{fieldErrors.odometerKm}</p>}
                </div>

                <div className="truck-fuel-field">
                  <label>{t("fuel.fields.litres", "Litros adicionados")}*</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.litresAdded}
                    onChange={(e) => handleFormChange("litresAdded", e.target.value)}
                    className={fieldErrors.litresAdded ? "input-error" : ""}
                    placeholder="75"
                  />
                  {fieldErrors.litresAdded && <p>{fieldErrors.litresAdded}</p>}
                </div>

                <div className="truck-fuel-field">
                  <label>{t("fuel.fields.pricePerLitre", "Preço por litro")}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={form.pricePerLitre}
                    onChange={(e) => handleFormChange("pricePerLitre", e.target.value)}
                    className={fieldErrors.pricePerLitre ? "input-error" : ""}
                    placeholder="1.6000"
                  />
                  {fieldErrors.pricePerLitre && <p>{fieldErrors.pricePerLitre}</p>}
                </div>

                <div className="truck-fuel-field">
                  <label>{t("fuel.fields.totalCost", "Custo total")}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.totalCost}
                    onChange={(e) => handleFormChange("totalCost", e.target.value)}
                    className={fieldErrors.totalCost ? "input-error" : ""}
                    placeholder="120.00"
                  />
                  {fieldErrors.totalCost && <p>{fieldErrors.totalCost}</p>}
                </div>

                <div className="truck-fuel-field">
                  <label>{t("fuel.fields.station", "Posto")}</label>
                  <input
                    type="text"
                    value={form.stationName}
                    onChange={(e) => handleFormChange("stationName", e.target.value)}
                    placeholder="Galp, BP, Repsol..."
                  />
                </div>
              </div>

              <label className="truck-fuel-checkbox">
                <input
                  type="checkbox"
                  checked={form.isFullTank}
                  onChange={(e) => handleFormChange("isFullTank", e.target.checked)}
                />
                <span>{t("fuel.fields.fullTank", "Depósito ficou cheio")}</span>
              </label>

              <div className="truck-fuel-field">
                <label>{t("fuel.fields.notes", "Notas")}</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => handleFormChange("notes", e.target.value)}
                  placeholder={t("fuel.fields.notesPlaceholder", "Observações opcionais...")}
                />
              </div>

              <div className="truck-fuel-modal-actions">
                <button type="button" className="truck-fuel-cancel-btn" onClick={closeModal}>
                  {t("fuel.cancel", "Cancelar")}
                </button>
                <button type="submit" className="truck-fuel-save-btn" disabled={saving}>
                  {saving
                    ? t("fuel.saving", "A guardar...")
                    : t("fuel.save", "Guardar")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

export default TruckFuelTab;